'use client'

import type { Address } from 'viem'

import { Avatar, type AvatarProps } from '@repo/ui'
import { getTokenIconUrl } from '@repo/utils'
// `.js`: Node ESM (the Playwright suites import @repo/web3) can't resolve `next/image`, which has no exports map.
import Image from 'next/image.js'
import { type ComponentProps, useCallback, useState } from 'react'

import type { ChainId, Token } from '../web3.types'

import { ChainIcon } from './ChainIcon'

const TOKEN_LOGO_ORIGIN = 'https://cdn.morpho.org/'

export type TokenIconProps = ComponentProps<typeof TokenIcon>

/** Token fields TokenIcon reads. */
export type TokenIconSource = Pick<Token, 'symbol' | 'logoUrl'> & { address?: Address }

export function TokenIcon({
  address,
  symbol,
  logoUrl,
  chainId,
  ...props
}: {
  address?: string | Address
  symbol?: string | null
  logoUrl?: string | null
  chainId?: ChainId | number
} & AvatarProps) {
  // Prefer the API-provided logo URL, falling back to the symbol-derived CDN URL.
  const candidates = [logoUrl, symbol ? getTokenIconUrl(symbol) : null].filter(
    (url, index, urls): url is string => !!url && urls.indexOf(url) === index
  )

  // Failed URLs are remembered across tokens — a 404'd logo is broken for everyone.
  const [failedUrls, setFailedUrls] = useState<ReadonlySet<string>>(() => new Set())
  const src = candidates.find(url => !failedUrls.has(url)) ?? null

  const markFailed = useCallback(
    (url: string) => {
      setFailedUrls(prev => (prev.has(url) ? prev : new Set(prev).add(url)))
    },
    [setFailedUrls]
  )

  const fallbackText = symbol
    ? symbol.slice(0, 1).toUpperCase()
    : address
      ? address.slice(2, 3).toUpperCase()
      : '0x'

  return (
    <div className="relative inline-flex">
      <Avatar {...props}>
        {/* Letter fallback sits behind the image: visible while the (opaque)
            logo loads and if it fails. Always rendered so we don't depend on the
            image's `onLoad` firing — a cached image can fire it before hydration
            attaches the handler, which would otherwise strand the fallback. */}
        <span
          aria-hidden
          className="bg-muted text-4xs flex size-full items-center justify-center rounded-full select-none"
        >
          {fallbackText}
        </span>
        {/* next/image renders a plain <img> in the SSR HTML, so the browser fetches
            it during parse rather than after hydration. Raster logos go through the
            optimizer at 48px (96px at 2x, enough for the largest drawn icon); SVGs and
            off-CDN URLs, which the apps' optimizers may not allow, pass through as-is.
            next/image replays errors missed before hydration, so `onError` covers
            icons that fail during SSR. */}
        {src && (
          <Image
            key={src}
            src={src}
            alt={`${symbol} icon`}
            width={48}
            height={48}
            loading="eager"
            unoptimized={!src.startsWith(TOKEN_LOGO_ORIGIN)}
            className="absolute inset-0 aspect-square size-full object-cover"
            onError={() => markFailed(src)}
          />
        )}
      </Avatar>
      {chainId != null && (
        <ChainIcon
          chainId={chainId}
          className="ring-background absolute -right-0.5 -bottom-0.5 size-[40%] min-h-2.5 min-w-2.5 rounded-full ring-1"
        />
      )}
    </div>
  )
}
